import { expensesModule } from "@nabble/module-expenses";
import { observabilityModule } from "@nabble/module-observability";
import { reportsModule } from "@nabble/module-reports";
import { driveModule } from "@nabble/module-drive";
import { tendersModule } from "@nabble/module-tenders";
import { providerProspectsModule } from "@nabble/module-provider-prospects";
import { chatModule } from "@nabble/module-chat";
import { websiteModule } from "@nabble/module-website";
import { qrCardsModule } from "@nabble/module-qr-cards";
import { vaultModule } from "@nabble/module-vault";
import { projectManagerModule } from "@nabble/module-project-manager";
import { isMajorCompatible, type BackendModuleDescriptor, type FrontModule } from "@nabble/sdk";

/**
 * The compiled list of FrontModules bundled into the shell. Adding a module
 * requires exactly one line here (see CLAUDE.md, "Adding a new module").
 */
export const modules: FrontModule[] = [observabilityModule, projectManagerModule, vaultModule, qrCardsModule, websiteModule, chatModule, providerProspectsModule, expensesModule, driveModule, tendersModule, reportsModule];

/**
 * The module whose public pages are the main domain's website (site.html, docs/adr/0016). The
 * edge serves that domain apart from the suite; changing who owns it is this one line.
 */
export const mainDomainModule: FrontModule | undefined = modules.find((m) => m.id === "website");

/**
 * Computes the modules the current user may see: present in the frontend
 * bundle AND returned by the backend (enabled) AND the user has the
 * module's permission. Unknown backend ids are logged and ignored; a major
 * version mismatch is logged and the module is not loaded.
 */
export function resolveVisibleModules(
  backendModules: BackendModuleDescriptor[],
  userPermissions: string[],
): FrontModule[] {
  const backendById = new Map(backendModules.map((m) => [m.id, m]));

  return modules.filter((frontModule) => {
    const backendModule = backendById.get(frontModule.id);
    if (!backendModule) {
      // Present in the bundle but not returned by the backend (disabled or
      // not installed there). Not an error: just hide it.
      return false;
    }

    if (!isMajorCompatible(frontModule.version, backendModule.version)) {
      console.warn(
        `[nabble] module "${frontModule.id}" major version mismatch: frontend ${frontModule.version} vs backend ${backendModule.version}. Not loading.`,
      );
      return false;
    }

    if (!userPermissions.includes(frontModule.permission)) {
      return false;
    }

    return true;
  });
}

/** Logs a warning for every backend module id that isn't present in the frontend bundle. */
export function warnUnknownBackendModules(backendModules: BackendModuleDescriptor[]): void {
  const knownIds = new Set(modules.map((m) => m.id));
  for (const backendModule of backendModules) {
    if (!knownIds.has(backendModule.id)) {
      console.warn(`[nabble] backend returned unknown module id "${backendModule.id}". Ignoring.`);
    }
  }
}

/**
 * Entering a module by its base path (e.g. /observability) lands on its first
 * section. Returns [basePath, firstSectionPath] for modules whose own routes
 * don't already cover the base path.
 */
export function moduleLandingRedirects(list: FrontModule[] = modules): [string, string][] {
  const out: [string, string][] = [];
  for (const m of list) {
    const first = m.menu[0]?.path;
    if (!first) continue;
    const base = `/${first.split("/").filter(Boolean)[0] ?? ""}`;
    if (base === "/" || base === first) continue;
    if (m.routes.some((r) => r.path === base)) continue;
    out.push([base, first]);
  }
  return out;
}
